import {
  ColorPicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Select,
  type SelectOptionType,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import type { ClassTutorsConnectFieldInput, ClassUpdateInput, InputMaybe } from 'client/gql/graphql';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useCreateSelectOptions } from 'hooks/useCreateSelectOptions';
import type { BpClassType } from 'pages/Institution/subpages/InstitutionClassesSubpage';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CombinedError, useMutation, useQuery } from 'urql';
import { colorPickerOptions, type UsedColors } from 'utils/colorHelper';
import { getGradeOptions } from 'utils/gradesHelper';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';

const CreateClassDocumentMutation = graphql(`
  mutation CreateClass($input: [ClassCreateInput!]!) {
    createClasses(input: $input) {
      classes {
        uuid
        __typename
      }
    }
  }
`);

const UpdateClassDocumentMutation = graphql(`
  mutation UpdateClassInClassForm($update: ClassUpdateInput!, $where: ClassWhere!) {
    updateClasses(update: $update, where: $where) {
      classes {
        uuid
        __typename
        name
        grade
        gradeGroup
        color
        tutors {
          uuid
          __typename
        }
        updatedAt
      }
    }
  }
`);

type ClassesFormProps = {
  selectedClass?: BpClassType;
  schoolYearUuid: string;
  colorsInUse: (UsedColors | null)[];
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

type Tutors = {
  one: string | null;
  two: string | null;
  three: string | null;
};

const ClassFormQueryDocument = graphql(`
  query ClassFormData($organizationUuid: ID!) {
    profiles(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      organizationRoles
      displayName
    }
    schoolYears(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      name
    }
  }
`);

export const ClassForm = ({ selectedClass, schoolYearUuid, colorsInUse, onLoad, onClose }: ClassesFormProps) => {
  const { t } = useTranslation();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();

  const [tutorUuids, setTutorUuids] = useState<Tutors>({
    one: null,
    two: null,
    three: null,
  });

  const [, create] = useMutation(CreateClassDocumentMutation);
  const [, update] = useMutation(UpdateClassDocumentMutation);

  const [{ data }] = useQuery({
    query: ClassFormQueryDocument,
    variables: { organizationUuid },
  });

  const initialValues: BpClassType = {
    uuid: selectedClass?.uuid ?? '',
    name: selectedClass?.name ?? '',
    shortName: selectedClass?.shortName ?? '',
    grade: selectedClass?.grade ?? 0,
    gradeGroup: selectedClass?.gradeGroup ?? '',
    tutor1Uuid: selectedClass?.tutor1Uuid ?? '',
    tutor2Uuid: selectedClass?.tutor2Uuid ?? '',
    tutor3Uuid: selectedClass?.tutor3Uuid ?? '',
    color: {
      color: selectedClass?.color.color ?? '',
      colorLabel: selectedClass?.color.colorLabel ?? '',
    },
    source: selectedClass?.uuid ?? '',
    divisionUuids: selectedClass?.divisionUuids ?? [],
    profileUuids: selectedClass?.profileUuids ?? [],
    schoolYearUuid: selectedClass?.schoolYearUuid ?? schoolYearUuid,
  };

  const profiles = data?.profiles;
  const schoolYears = data?.schoolYears;

  const schoolYearOptions = useCreateSelectOptions(schoolYears, 'uuid', 'name');
  const selectableTutors = profiles?.filter(
    (p) => p.organizationRoles.includes('TEACHER') || p.organizationRoles.includes('OTHER'),
  );
  const tutorOptions = useCreateSelectOptions(selectableTutors, 'uuid', 'displayName');
  const gradeOptions = getGradeOptions();

  const uniqueTeacherOptions: SelectOptionType[] = useMemo(() => {
    let unique = selectableTutors;
    if (tutorUuids.one) unique = unique?.filter((t) => t.uuid !== tutorUuids.one);
    if (tutorUuids.two) unique = unique?.filter((t) => t.uuid !== tutorUuids.two);
    if (tutorUuids.three) unique = unique?.filter((t) => t.uuid !== tutorUuids.three);
    return (
      unique?.map((t) => {
        return { value: t.uuid, label: t.displayName ?? '' };
      }) ?? []
    );
  }, [selectableTutors, tutorUuids.one, tutorUuids.two, tutorUuids.three]);

  const ColorPickerData = colorPickerOptions({
    htmlColorsInUse: colorsInUse,
    currentColor: selectedClass?.color.color ?? '',
    currentColorUsedBy: selectedClass?.name,
    forTypename: 'Class',
  });

  const handleSubmit = async (values: BpClassType, formHelpers: FormikHelpers<BpClassType>) => {
    let hasError = false;
    if (!values.name) {
      formHelpers.setFieldError('name', t('validation.required.designation'));
      hasError = true;
    }
    if (!values.grade) {
      formHelpers.setFieldError('grade', t('validation.required.grade'));
      hasError = true;
    }
    if (!values.shortName) {
      formHelpers.setFieldError('shortName', t('validation.required.shortName'));
      hasError = true;
    }
    if (hasError) {
      return;
    }

    onLoad(true);

    let result: { error?: CombinedError | undefined };

    const tutors: InputMaybe<Array<ClassTutorsConnectFieldInput>> = [];
    if (values.tutor1Uuid) {
      tutors.push({
        edge: {
          order: 1,
        },
        where: {
          node: {
            uuid: values.tutor1Uuid,
          },
        },
      });
    }
    if (values.tutor2Uuid) {
      tutors.push({
        edge: {
          order: 2,
        },
        where: {
          node: {
            uuid: values.tutor2Uuid,
          },
        },
      });
    }
    if (values.tutor3Uuid) {
      tutors.push({
        edge: {
          order: 3,
        },
        where: {
          node: {
            uuid: values.tutor3Uuid,
          },
        },
      });
    }

    const now = new Date().toISOString();
    if (!selectedClass) {
      const data = {
        createdAt: now,
        updatedAt: now,
        name: values.name,
        grade: values.grade.toString(),
        gradeGroup: values.gradeGroup,
        shortName: values.shortName,
        color: values.color.color,
        managedBy: 'bp',
        tutors: { connect: tutors },
      };

      result = await create({
        input: {
          ...data,
          schoolYear: {
            connect: { where: { node: { uuid: values.schoolYearUuid } } },
          },
          foreignRefId: [],
          organization: {
            connect: {
              where: {
                node: {
                  uuid: organizationUuid,
                },
              },
            },
          },
        },
      });
    } else {
      const data: ClassUpdateInput = {
        name: values.name,
        grade: values.grade.toString(),
        gradeGroup: values.gradeGroup,
        color: values.color.color,
        shortName: values.shortName,
        schoolYear: {
          disconnect: {},
          connect: { where: { node: { uuid: values.schoolYearUuid } } },
        },
        tutors: [
          {
            disconnect: [{}],
            connect: tutors,
          },
        ],
        updatedAt: now,
      };

      result = await update({
        where: {
          uuid: selectedClass.uuid,
        },
        update: data,
      });
    }

    if (!result || result.error) {
      showErrorToast({
        name: 'Error',
        message: 'mutationResult.error',
        graphQLErrors: [],
      });
    } else {
      if (selectedClass) {
        showSuccessToast(t('classes.classEdited'));
      } else {
        showSuccessToast(t('classes.classCreated'));
      }
    }

    onLoad(false);
    formHelpers.resetForm();
    onClose();
  };

  useEffect(() => {
    const tutors: Tutors = {
      one: selectedClass?.tutor1Uuid ?? null,
      two: selectedClass?.tutor2Uuid ?? null,
      three: selectedClass?.tutor3Uuid ?? null,
    };
    setTutorUuids(tutors);
  }, [selectedClass?.tutor1Uuid, selectedClass?.tutor2Uuid, selectedClass?.tutor3Uuid]);

  return (
    <Formik<BpClassType>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnBlur={true}
      enableReinitialize={true}
    >
      {({ errors, values, setFieldValue, isSubmitting, dirty, isValidating, handleSubmit }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={8}>
                  <Input
                    name='name'
                    value={values.name}
                    label={t('common.designation')}
                    onChange={async (e) => await setFieldValue('name', e.target.value)}
                    required
                    error={errors.name as string}
                  />
                </GridColumn>
                <GridColumn width={2}>
                  <Select
                    options={schoolYearOptions}
                    onChange={async (e) => {
                      const uuid = e && ((e as SelectOptionType).value as string);
                      await setFieldValue('schoolYearUuid', uuid);
                    }}
                    value={schoolYearOptions.find((y) => y.value === values.schoolYearUuid)}
                    name='schoolYear'
                    label={t('schoolyears.titleSingular')}
                    menuPosition='fixed'
                  />
                </GridColumn>
                <GridColumn width={2}>
                  <ColorPicker
                    name='color'
                    label={t('common.color')}
                    options={ColorPickerData.groupedColorOptions}
                    maxMenuHeight={300}
                    menuPosition='fixed'
                    onChange={async (option) => {
                      await setFieldValue('color.color', option?.html);
                    }}
                    defaultValue={ColorPickerData.currentColor}
                    error={errors.color as string}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s' spacingBottom='s'>
                <GridColumn width={4}>
                  <Select
                    options={gradeOptions}
                    onChange={async (e) => {
                      const grade = e && ((e as SelectOptionType).value as number);
                      await setFieldValue('grade', grade);
                    }}
                    value={gradeOptions.find((g) => g.value === String(values.grade))}
                    name='grade'
                    label={t('classes.grade')}
                    menuPosition='fixed'
                    required
                    error={errors.grade as string}
                  />
                </GridColumn>
                <GridColumn width={4}>
                  <Input
                    name='gradeGroup'
                    value={values.gradeGroup}
                    label={t('classes.gradeGroup')}
                    onChange={async (e) => await setFieldValue('gradeGroup', e.target.value)}
                  />
                </GridColumn>
                <GridColumn width={4}>
                  <Input
                    name='shortNmae'
                    value={values.shortName}
                    label={t('classes.shortName')}
                    onChange={async (e) => await setFieldValue('shortName', e.target.value)}
                    required
                    error={errors.shortName as string}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s'>
                <GridColumn width={4}>
                  <Select
                    options={uniqueTeacherOptions}
                    onChange={async (e) => {
                      const uuid = e && ((e as SelectOptionType).value as string);
                      if (uuid) {
                        setTutorUuids({ ...tutorUuids, one: uuid });
                      } else {
                        setTutorUuids({ one: null, two: null, three: null });
                      }
                      await setFieldValue('tutor1Uuid', uuid);
                    }}
                    value={tutorOptions.find((t) => t.value === values.tutor1Uuid)}
                    name='tutor1'
                    label={t('classes.tutor') + ' 1'}
                    menuPosition='fixed'
                    isClearable
                    isSearchable
                  />
                </GridColumn>
                <GridColumn width={4}>
                  <Select
                    options={uniqueTeacherOptions}
                    onChange={async (e) => {
                      const uuid = e && ((e as SelectOptionType).value as string);
                      if (uuid) {
                        setTutorUuids({ ...tutorUuids, two: uuid });
                      } else {
                        setTutorUuids({
                          ...tutorUuids,
                          two: null,
                          three: null,
                        });
                      }
                      await setFieldValue('tutor2Uuid', uuid);
                    }}
                    value={tutorOptions.find((t) => t.value === values.tutor2Uuid)}
                    name='tutor2'
                    label={t('classes.tutor') + ' 2'}
                    menuPosition='fixed'
                    isClearable
                    disabled={!tutorUuids.one}
                  />
                </GridColumn>
                <GridColumn width={4}>
                  <Select
                    options={uniqueTeacherOptions}
                    onChange={async (e) => {
                      const uuid = e && ((e as SelectOptionType).value as string);
                      if (uuid) {
                        setTutorUuids({ ...tutorUuids, three: uuid });
                      } else {
                        setTutorUuids({ ...tutorUuids, three: null });
                      }
                      await setFieldValue('tutor3Uuid', uuid);
                    }}
                    value={tutorOptions.find((t) => t.value === values.tutor3Uuid)}
                    name='tutor3'
                    label={t('classes.tutor') + ' 3'}
                    menuPosition='fixed'
                    isClearable
                    disabled={!tutorUuids.two}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: onClose,
              }}
              submitButton={{
                disabled: isSubmitting || !dirty || isValidating,
                callback: () => {
                  handleSubmit();
                },
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
